import { useState } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { reviews, googleRating } from "../data/reviews";
import { Button } from "./ui/button";

export const Reviews = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const reviewsPerPage = 3;
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);

  const nextPage = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const visibleReviews = reviews.slice(
    currentIndex * reviewsPerPage,
    (currentIndex + 1) * reviewsPerPage
  );

  const renderStars = (rating) => {
    return Array(5).fill(0).map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-zinc-300'}`}
      />
    ));
  };

  return (
    <section className="section-padding bg-zinc-50" data-testid="reviews-section">
      <div className="container-custom">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="text-yellow-500 font-bold uppercase tracking-wider text-sm mb-4 block">
              Témoignages
            </span>
            <h2 className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-zinc-900 font-heading">
              Avis de nos clients
            </h2>
          </div>

          {/* Google Rating Badge */}
          <div className="flex items-center gap-4 bg-white border border-zinc-200 rounded-sm px-6 py-4">
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Google_2015_logo.svg/200px-Google_2015_logo.svg.png"
              alt="Google"
              className="h-6 object-contain"
            />
            <div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-zinc-900">{googleRating.score}</span>
                <div className="flex">{renderStars(5)}</div>
              </div>
              <p className="text-sm text-zinc-500">{googleRating.totalReviews} avis</p>
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {visibleReviews.map((review) => (
            <div
              key={review.id}
              className="bg-white border border-zinc-200 rounded-sm p-6 transition-all duration-300 hover:shadow-lg hover:border-zinc-300"
              data-testid={`review-card-${review.id}`}
            >
              {/* Quote icon */}
              <Quote className="w-8 h-8 text-yellow-400 mb-4" />

              {/* Review text */}
              <p className="text-zinc-600 mb-6 text-sm leading-relaxed line-clamp-4">
                "{review.text}"
              </p>

              {/* Author */}
              <div className="flex items-center justify-between pt-4 border-t border-zinc-100">
                <div>
                  <p className="font-bold text-zinc-900">{review.author}</p>
                  <p className="text-xs text-zinc-400">{review.date}</p>
                </div>
                <div className="flex">{renderStars(review.rating)}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={prevPage}
            className="border-zinc-300 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white"
            data-testid="reviews-prev-button"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>

          <div className="flex gap-2">
            {Array(totalPages).fill(0).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentIndex ? 'bg-yellow-400 w-6' : 'bg-zinc-300 hover:bg-zinc-400'
                }`}
                data-testid={`reviews-dot-${i}`}
              />
            ))}
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={nextPage}
            className="border-zinc-300 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white"
            data-testid="reviews-next-button"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
